/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { inject, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { MessageBasket } from '@gh/core-messages';
import { DialogRouteSnapshot, NotificationService, WaitingMode, WaitingService } from '@gh/core-ui';
import { expandObservable } from '@gh/core-util';
import { throwError$ } from '@gh/rx';
import { catchError$, delay$, map$ } from '@gh/rx/operators';
import { isFunction } from 'lodash';
import { Observable } from 'rxjs';
import { AppRouter } from './app-router.service';

function callInitView(service: any, selector: string | ((...args: string[]) => any), ...args: any[]): any {
  if (isFunction(selector)) {
    return selector(service, ...args);
  } else {
    return service[selector](...args);
  }
}

export const initViewResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | any => {
  const injector = inject(Injector);
  const waitingService = inject(WaitingService);
  const notificationService = inject(NotificationService);
  const router = inject(AppRouter);

  const initView = route.data['initView'];

  if (initView) {
    const { service, selector } = initView.service
      ? initView
      : { service: initView, selector: void 0 };

    const serviceInstance = injector.get(service);
    const ob$ = expandObservable(callInitView(serviceInstance, selector || 'initView', route, state));
    const obWithWaiting$ = initView.noWaiting
      ? ob$
      : ob$.pipe(waitingService.operator(WaitingMode.Background));
    return obWithWaiting$.pipe(
      catchError$((errors) => {
        if (errors instanceof MessageBasket && errors.has('msg_resource_not_found')) {
          router.navigateByUrl('/404');
          return [];
        } else {
          notificationService.processAll(errors);
          return throwError$(errors);
        }
      }),
      delay$(0));
  } else {
    return void 0;
  }
};
export function initDialogViewResolverFn(route: DialogRouteSnapshot<any>): Observable<any> | any | void {
  const injector = inject(Injector);
  const waitingService = inject(WaitingService);
  const notificationService = inject(NotificationService);

  const initView = route.routeConfig.data['initView'];

  if (initView) {
    const { service, selector } = initView.service
      ? initView
      : { service: initView, selector: void 0 };

    const serviceInstance = injector.get(service);
    const ob$ = expandObservable(callInitView(serviceInstance, selector || 'initDialogView', route));
    const obWithWaiting$ = initView.noWaiting
      ? ob$
      : ob$.pipe(waitingService.operator(WaitingMode.Background));
    return obWithWaiting$.pipe(
      map$((data: any) => ({ init: data })),
      catchError$((errors) => {
        notificationService.processAll(errors);
        return throwError$(errors);
      }));
  } else {
    return void 0;
  }
}
